/**
 * dataLayer.js — Central GA4 ecommerce tracking via GTM dataLayer.
 *
 * Exports event functions + init helpers for use across JS modules and Twig templates.
 * Exposed on window.__avariDataLayer for inline / Alpine access.
 */

function push(event, data = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
    window.dataLayer.push({ event, ...data });
}

const RECENT_BOOKING_CUE_STORAGE_KEY = 'avari_recent_booking_cue';

function getRecentBookingCueContext(flow) {
    try {
        const context = JSON.parse(sessionStorage.getItem(RECENT_BOOKING_CUE_STORAGE_KEY) || 'null');
        return context?.listingId && context.listingId === flow.listingId ? context : null;
    } catch {
        return null;
    }
}

// Round monetary values to 2 decimals. Returns Number (not string).
function money(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.round((n + Number.EPSILON) * 100) / 100;
}

// US state full name → 2-letter code (also passes through 2-letter codes).
const US_STATES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas',
    CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
    DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii',
    ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine',
    MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska',
    NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
    NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
    SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
    UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
    WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

function stateAbbr(state) {
    const clean = (state || '').toString().trim();
    if (!clean) return '';
    if (clean.length === 2 && US_STATES[clean.toUpperCase()]) return clean.toUpperCase();
    const upper = clean.toUpperCase();
    for (const [abbr, fullName] of Object.entries(US_STATES)) {
        if (fullName.toUpperCase() === upper) return abbr;
    }
    return clean;
}

function formatCityState(city, state) {
    const c = (city || '').toString().trim();
    const s = stateAbbr(state);
    if (c && s) return `${c}, ${s}`;
    return c || s || '';
}

function getTrackingTax(flow) {
    return Number(flow.quote?.tax ?? flow.quote?.taxAmount ?? flow.quote?.taxes ?? 0);
}

function getTrackingNights(flow) {
    return Number(flow.quote?.nightsCount ?? 1) || 1;
}

function getTrackingValue(flow) {
    return Number(flow.quote?.total ?? 0);
}

function getTrackingItemPrice(flow) {
    const nightsCount = getTrackingNights(flow);
    const nightlyRate = Number(flow.nightlyRate ?? NaN);

    if (Number.isFinite(nightlyRate) && nightlyRate > 0) {
        return nightlyRate;
    }

    const basePrice = Number(flow.quote?.basePrice ?? flow.quote?.money?.fareAccommodation ?? NaN);
    if (Number.isFinite(basePrice) && nightsCount > 0) {
        return basePrice / nightsCount;
    }

    const total = getTrackingValue(flow);
    const tax = getTrackingTax(flow);
    return nightsCount > 0 ? Math.max(0, total - tax) / nightsCount : total;
}

function buildTrackingItem(flow) {
    return {
        item_id: flow.listingId || '',
        item_name: flow.itemName || '',
        item_category: flow.itemCategory || '',
        price: money(getTrackingItemPrice(flow)),
        quantity: getTrackingNights(flow),
    };
}

// ─── Event functions ───

/**
 * search_initiated — fired when user submits a new search on the search page.
 */
export function searchInitiated(store) {
    const [checkIn, checkOut] = store.parseDates();
    push('search_initiated', {
        search_destination: store.where,
        search_check_in: checkIn,
        search_check_out: checkOut,
        search_nights: store.nights,
        search_guests: store.guests,
        search_adults: store.adults,
        search_children: store.children,
        search_pets: store.pets,
    });
}

/**
 * view_item_list — fired when search results are rendered.
 */
export function viewItemList(store) {
    const items = (store.results.listings || []).map((listing, index) => {
        const id = listing.guestyListingId || listing.id;
        const rawPrice = store.getListingPrice(id) || listing.basePrice;
        return {
            item_id: id,
            item_name: listing.title || listing.unitName || '',
            item_category: formatCityState(listing.city, listing.state) || store.where || '',
            index: index,
            price: rawPrice != null ? money(rawPrice) : undefined,
        };
    });

    push('view_item_list', {
        ecommerce: {
            item_list_name: 'search_results',
            items,
        },
    });
}

/**
 * availability_scarcity_impression — fired when a verified search cue is shown.
 */
export function availabilityScarcityImpression(store) {
    const urgency = store.results?.urgency;
    if (!urgency?.enabled) return;

    push('availability_scarcity_impression', {
        scarcity_level: urgency.level,
        scarcity_percentage: urgency.percentage,
        scarcity_available_count: urgency.availableCount,
        scarcity_eligible_count: urgency.eligibleCount,
        search_destination: store.where,
        search_check_in: store.parseDates?.()[0] || null,
        search_check_out: store.parseDates?.()[1] || null,
    });
}

/**
 * availability_scarcity_dismissed — fired when a visitor dismisses a cue.
 */
export function availabilityScarcityDismissed(store) {
    const urgency = store.results?.urgency;
    push('availability_scarcity_dismissed', {
        scarcity_level: urgency?.level || null,
        scarcity_percentage: urgency?.percentage ?? null,
        search_destination: store.where,
    });
}

export function rareFindImpression(flow) {
    const metric = flow.rareFindMetric;
    if (!metric?.enabled) return;
    push('rare_find_impression', {
        item_id: flow.listingId || '',
        availability_percentage: metric.percentage,
        eligible_nights: metric.eligibleNights,
        available_nights: metric.availableNights,
        selected_check_in: flow.checkIn || null,
        selected_check_out: flow.checkOut || null,
    });
}

export function nearbyDateSuggestionsImpression(flow, suggestions) {
    push('nearby_date_suggestions_impression', {
        item_id: flow.listingId || '',
        requested_check_in: flow.checkIn || null,
        requested_check_out: flow.checkOut || null,
        suggestion_count: suggestions.length,
    });
}

export function nearbyDateSuggestionSelected(flow, suggestion) {
    push('nearby_date_suggestion_selected', {
        item_id: flow.listingId || '',
        requested_check_in: flow.checkIn || null,
        requested_check_out: flow.checkOut || null,
        suggested_check_in: suggestion?.checkIn || null,
        suggested_check_out: suggestion?.checkOut || null,
        shift_days: Number(suggestion?.shiftDays || 0),
    });
}

/**
 * select_item — fired when a user clicks a property card.
 */
export function selectItem(itemData, listName) {
    push('select_item', {
        ecommerce: {
            item_list_name: listName || '',
            items: [{
                item_id: itemData.item_id || '',
                item_name: itemData.item_name || '',
                item_category: itemData.item_category || '',
                index: itemData.index != null ? Number(itemData.index) : undefined,
                price: itemData.price != null ? money(itemData.price) : undefined,
            }],
        },
    });
}

/**
 * view_item — fired on listing detail pages.
 * Reads static data from a #listing-view-data JSON block + URL params for nights.
 */
export function viewItem(data) {
    const params = new URLSearchParams(window.location.search);
    const dates = params.get('dates');
    let nights = null;

    if (dates && dates.includes(' to ')) {
        const [checkIn, checkOut] = dates.split(' to ').map(d => d.trim());
        const start = new Date(checkIn + 'T00:00:00');
        const end = new Date(checkOut + 'T00:00:00');
        if (!isNaN(start) && !isNaN(end)) {
            nights = Math.round((end - start) / 86400000);
        }
    }

    const item = {
        item_id: data.item_id || '',
        item_name: data.item_name || '',
        item_category: data.item_category || '',
        price: data.base_price != null ? money(data.base_price) : undefined,
    };

    if (nights) {
        item.quantity = nights;
    }

    push('view_item', {
        ecommerce: {
            items: [item],
        },
    });
}

/**
 * begin_checkout — fired after quote review succeeds on checkout.
 */
export function beginCheckout(flow) {
    const total = getTrackingValue(flow);
    const tax = getTrackingTax(flow);
    const recentBookingCue = getRecentBookingCueContext(flow);

    push('begin_checkout', {
        ecommerce: {
            value: money(total),
            tax: money(tax),
            currency: flow.currency || 'USD',
            items: [buildTrackingItem(flow)],
        },
        check_in_date: flow.checkIn || '',
        check_out_date: flow.checkOut || '',
        guest_count: (flow.adults || 0) + (flow.children || 0),
        is_authenticated: !!flow.currentUser,
        recent_booking_cue_seen: !!recentBookingCue,
    });
}

export function recentBookingCueCheckoutStarted(flow) {
    const context = getRecentBookingCueContext(flow);
    if (!context) return;

    push('recent_booking_cue_checkout_start', {
        item_id: flow.listingId || '',
        selected_check_in: context.checkIn || flow.checkIn || null,
        selected_check_out: context.checkOut || flow.checkOut || null,
    });
}

/**
 * update_item_dates — fired on listing pages after a date refresh updates quote details.
 */
export function updateItemDates(flow) {
    push('update_item_dates', {
        check_in_date: flow.checkIn || '',
        check_out_date: flow.checkOut || '',
        guest_count: (flow.adults || 0) + (flow.children || 0),
        ecommerce: {
            currency: flow.currency || 'USD',
            value: money(getTrackingValue(flow)),
            items: [buildTrackingItem(flow)],
        },
    });
}

export function trackReviewEvent(flow) {
    if (flow.trackingContext === 'checkout' || window.location.pathname.startsWith('/checkout')) {
        beginCheckout(flow);
        return;
    }

    updateItemDates(flow);
}

/**
 * login / sign_up — fired on HTMX auth events.
 */
export function authEvent(eventName, method) {
    const isCheckout = window.location.pathname.startsWith('/checkout');
    push(eventName, {
        method: method || 'email',
        checkout_context: isCheckout ? 'checkout' : 'site',
    });
}

/**
 * add_payment_info — fired after payment tokenization or saved method selection.
 */
export function addPaymentInfo(flow, paymentType) {
    const total = getTrackingValue(flow);
    const tax = getTrackingTax(flow);

    push('add_payment_info', {
        ecommerce: {
            value: money(total),
            tax: money(tax),
            currency: flow.currency || 'USD',
            payment_type: paymentType || 'credit_card',
            items: [buildTrackingItem(flow)],
        },
    });
}

/**
 * purchase — fired from bookingFlow at the moment of booking success,
 * before the redirect to the confirmation page. Ensures the event fires
 * exactly once per reservation, tied to the actual order placement.
 */
export function purchase(flow, transactionId) {
    push('purchase', {
        transaction_id: transactionId || '',
        check_in_date: flow.checkIn || '',
        check_out_date: flow.checkOut || '',
        guest_count: (flow.adults || 0) + (flow.children || 0),
        ecommerce: {
            value: money(getTrackingValue(flow)),
            tax: money(getTrackingTax(flow)),
            currency: flow.currency || 'USD',
            items: [buildTrackingItem(flow)],
        },
    });
}

// ─── Init helpers ───

/**
 * Listen for HTMX login/signup events and push auth dataLayer events.
 */
export function initAuthListeners() {
    if (typeof htmx === 'undefined') return;

    htmx.on('loginSucceeded', () => {
        authEvent('login', 'email');
    });
    htmx.on('signupSucceeded', () => {
        authEvent('sign_up', 'email');
    });
}

/**
 * Event delegation for [data-track-select-item] clicks on destination pages.
 * Only fires when the card is inside a [data-item-list-name] ancestor.
 */
export function initCardClickTracking() {
    document.addEventListener('click', (e) => {
        const card = e.target.closest('[data-track-select-item]');
        if (!card) return;

        const listContainer = card.closest('[data-item-list-name]');
        const listName = listContainer?.dataset.itemListName || 'destination';

        selectItem({
            item_id: card.dataset.itemId || '',
            item_name: card.dataset.itemName || '',
            item_category: card.dataset.itemCategory || '',
            index: card.dataset.itemIndex != null ? Number(card.dataset.itemIndex) : undefined,
            price: card.dataset.itemPrice ? Number(card.dataset.itemPrice) : undefined,
        }, listName);
    });
}

/**
 * Read #listing-view-data JSON and push view_item on listing pages.
 */
export function initViewItem() {
    const el = document.getElementById('listing-view-data');
    if (!el) return;

    try {
        const data = JSON.parse(el.textContent);
        viewItem(data);
    } catch (e) {
        // Silently ignore malformed JSON
    }
}

// ─── Expose on window for Twig / Alpine inline access ───

window.__avariDataLayer = {
    searchInitiated,
    viewItemList,
    availabilityScarcityImpression,
    availabilityScarcityDismissed,
    rareFindImpression,
    recentBookingCueCheckoutStarted,
    nearbyDateSuggestionsImpression,
    nearbyDateSuggestionSelected,
    selectItem,
    viewItem,
    beginCheckout,
    updateItemDates,
    trackReviewEvent,
    authEvent,
    addPaymentInfo,
    purchase,
    initAuthListeners,
    initCardClickTracking,
    initViewItem,
    formatCityState,
};
